import React, { useState, useEffect } from 'react';
import { Slider, sliderClasses, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface LayerTreeItemSwiperSliderProps {
  swipeState?: 'L' | 'R';
  onChangeSwipeState?: (event: Event | React.SyntheticEvent<Element, Event>, newValue: null | 'L' | 'R') => void;
  marginRight: '20px' | '44px';
  isThumbDisabled: boolean;
  hideThumb: boolean;
}

const StyledSlider = styled(Slider, {
  shouldForwardProp: (prop) =>
    prop !== 'thumbColor' && prop !== 'thumbText' && prop !== 'marginRight' && prop !== 'hideThumb' && prop !== 'isThumbDisabled',
})<{
  thumbColor: 'secondary' | 'primary';
  thumbText: string;
  marginRight: string;
  hideThumb: boolean;
  isThumbDisabled: boolean;
}>(({ theme, thumbColor, thumbText, marginRight, hideThumb, isThumbDisabled }) => ({
  marginRight,
  width: '60px',

  [`& .${sliderClasses.rail}`]: {
    height: 6,
    borderRadius: 2,
  },

  [`& .${sliderClasses.thumb}`]: {
    display: hideThumb ? 'none' : 'flex', // <-- hide thumb if prop true
    height: 15,
    width: 15,
    backgroundColor: isThumbDisabled
      ? theme.palette.action.disabledBackground
      : thumbColor === 'primary'
      ? theme.palette.primary.main
      : theme.palette.secondary.main,
    color: isThumbDisabled
      ? theme.palette.action.disabled
      : thumbColor === 'primary'
      ? theme.palette.primary.contrastText
      : theme.palette.secondary.contrastText,
    fontSize: '11px',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: '15px',
    position: 'relative',

    '&:before': {
      content: `"${thumbText}"`,
      display: 'block',
      width: '100%',
      height: '100%',
      textAlign: 'center',
      lineHeight: '15px',
    },

    boxShadow: '0 0 2px 0px rgba(0, 0, 0, 0.1)',

    '&:focus, &:hover, &.Mui-active': {
      boxShadow: '0px 0px 3px 1px rgba(0, 0, 0, 0.1)',
    },
  },
}));

const LayerTreeItemSwiperSlider = ({
  swipeState,
  onChangeSwipeState,
  marginRight,
  isThumbDisabled,
  hideThumb,
}: LayerTreeItemSwiperSliderProps) => {
  const { t } = useTranslation();

  const mapValueToSwipeState = (value: number): 'L' | 'R' | null => {
    if (value === 1) return 'L';
    if (value === 2) return null;
    if (value === 3) return 'R';
    return null;
  };

  const mapSwipeStateToValue = (state?: 'L' | 'R'): number => {
    switch (state) {
      case 'L':
        return 1;
      case 'R':
        return 3;
      default:
        return 2;
    }
  };

  const [localValue, setLocalValue] = useState(mapSwipeStateToValue(swipeState));

  useEffect(() => {
    setLocalValue(mapSwipeStateToValue(swipeState));
  }, [swipeState]);

  const handleSliderChange = (event: Event | React.SyntheticEvent<Element, Event>, value: number | number[]) => {
    if (typeof value === 'number') {
      setLocalValue(value);
    }
  };

  const handleSliderChangeCommitted = (event: Event | React.SyntheticEvent<Element, Event>, value: number | number[]) => {
    if (typeof value === 'number' && onChangeSwipeState) {
      onChangeSwipeState(event, mapValueToSwipeState(value));
    }
  };

  const handleFormatLabel = (value: number) => {
    switch (value) {
      case 1:
        return t('layerswiper.left');
      case 2:
        return t('layerswiper.both');
      case 3:
        return t('layerswiper.right');
      default:
        return value;
    }
  };

  const thumbText = localValue === 1 ? t('layerswiper.left_short') : localValue === 3 ? t('layerswiper.right_short') : '';

  const thumbColor = localValue === 1 ? 'secondary' : localValue === 2 ? 'primary' : 'secondary';

  return (
    <StyledSlider
      disabled={isThumbDisabled}
      size="medium"
      value={localValue}
      valueLabelDisplay="auto"
      valueLabelFormat={handleFormatLabel}
      track={false}
      min={1}
      max={3}
      onChange={handleSliderChange}
      onChangeCommitted={handleSliderChangeCommitted}
      thumbColor={thumbColor}
      thumbText={thumbText}
      marginRight={marginRight}
      hideThumb={hideThumb}
      isThumbDisabled={isThumbDisabled}
    />
  );
};

export default LayerTreeItemSwiperSlider;
